/**
 * Pacific Time (America/Los_Angeles) helpers. Rule (2026-10-12): the ERP and the User Portal always display dates and times in
 * Pacific Time and label them "PT"; date-only strings stored in Firestore (for example a refund ticket's `sessionDate`) are the
 * Pacific calendar date as `YYYY-MM-DD`. Firestore Timestamps and ISO instants are absolute moments, so they stay as they are and
 * are only displayed in Pacific Time. Keep this file identical in projects/user-portal/src/app/utils/pacific-time.util.ts.
 */
export const PACIFIC_TIME_ZONE = 'America/Los_Angeles';
export const PACIFIC_LABEL = 'PT';

const DATE_ONLY = /^\d{4}[-/]\d{2}[-/]\d{2}$/;   // YYYY-MM-DD or YYYY/MM/DD: a calendar date with no time zone

/** Accepts a Date, ISO/date string, epoch milliseconds, or a Firestore Timestamp-like object; returns null when not a valid moment. */
function toMoment(value: any): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value.trim());
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') return toMoment(value.toDate());
  if (typeof value.seconds === 'number') return toMoment(value.seconds * 1000);
  return null;
}

export function isDateOnlyString(value: any): boolean {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

/** The Pacific calendar date (`YYYY-MM-DD`) of a moment. A plain `YYYY-MM-DD` string is already a Pacific date and is returned unchanged. */
export function toPacificDateString(value: any): string | null {
  if (isDateOnlyString(value)) return String(value).trim().replace(/\//g, '-');
  const d = toMoment(value);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: PACIFIC_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Today's Pacific calendar date (`YYYY-MM-DD`). */
export function todayInPacific(now: Date = new Date()): string {
  return toPacificDateString(now) as string;
}

/** `Oct 11, 2026 PT` for a moment, or for a `YYYY-MM-DD` Pacific date. Returns '-' when the value is empty/invalid. */
export function formatPacificDate(value: any): string {
  if (isDateOnlyString(value)) {
    const [y, m, d] = String(value).trim().split(/[-/]/).map(Number);
    const label = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d)));
    return `${label} ${PACIFIC_LABEL}`;
  }
  const d = toMoment(value);
  if (!d) return '-';
  return `${new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC_TIME_ZONE, year: 'numeric', month: 'short', day: 'numeric' }).format(d)} ${PACIFIC_LABEL}`;
}

/** `Oct 11, 2026, 5:41 PM PT` for a moment (seconds optional). Returns '-' when the value is empty/invalid. */
export function formatPacificDateTime(value: any, withSeconds = false): string {
  const d = toMoment(value);
  if (!d) return '-';
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: true
  }).format(d);
  return `${label} ${PACIFIC_LABEL}`;
}
